/**
 * Icons drawn to the design system's icon rules: 24px grid, 1.5px stroke, round caps
 * and joins (docs/07-DESIGN-SYSTEM.md). Stroke colour inherits from `currentColor` so
 * an icon always matches the text beside it.
 *
 * The Google mark is the exception — it is Google's own artwork and is reproduced
 * unmodified in its official colours, as docs/06-UI-SPEC.md §3 requires.
 */

export interface IconProps {
  readonly size?: number;
  readonly className?: string;
}

function strokeProps(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
}

/** A person — used on the "I'm a Teacher" card. */
export function TeacherIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20.25a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

/** A graduation cap — used on the "I'm a Student" card. */
export function StudentIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M2.25 9 12 4.5 21.75 9 12 13.5 2.25 9Z" />
      <path d="M6.75 11.1v4.65c0 1.66 2.35 3 5.25 3s5.25-1.34 5.25-3V11.1" />
      <path d="M21.75 9v5.25" />
    </svg>
  );
}

/** A plus — used on the "Start New Lesson" card. */
export function PlusIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M12 5.25v13.5M5.25 12h13.5" />
    </svg>
  );
}

/** An open folder — used on the "Open Previous Lesson" card. */
export function FolderIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4.19a1.5 1.5 0 0 1 1.06.44l1.31 1.31h7.44A1.5 1.5 0 0 1 20 9.25" />
      <path d="M3 7.5v10.25A1.25 1.25 0 0 0 4.25 19h14.02a1.5 1.5 0 0 0 1.45-1.11L21.4 11.4a1 1 0 0 0-.97-1.26H7.6a1.5 1.5 0 0 0-1.45 1.11L4.2 18.5" />
    </svg>
  );
}

/** A shelf of finished lessons — the empty-state mark on Teacher Home. */
export function LessonsEmptyIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <rect x="3.25" y="4.75" width="17.5" height="14.5" rx="2" />
      <path d="M3.25 9.25h17.5M8 4.75v4.5" />
      <path d="M7 13.5h6M7 16h9" />
    </svg>
  );
}

/** Google's "G" mark, in Google's colours, unaltered. */
export function GoogleMark({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className={className}>
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17Z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7A21.99 21.99 0 0 0 24 46Z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7Z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07Z"
      />
    </svg>
  );
}

/** A pen held nib-down — the Pen tool. */
export function PenIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M16.4 3.6a2 2 0 0 1 2.8 0l1.2 1.2a2 2 0 0 1 0 2.8L8.6 19.4 3.5 20.5l1.1-5.1Z" />
      <path d="M14.2 5.8 18.2 9.8" />
    </svg>
  );
}

/** A marker with a chisel nib — the Highlighter tool. */
export function HighlighterIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M13.9 5.1a2.2 2.2 0 0 1 3.1 0l1.9 1.9a2.2 2.2 0 0 1 0 3.1L8.4 20.6l-5 1 1-5Z" />
      <path d="M12 7 17 12" />
      <path d="M13 21.5h8" />
    </svg>
  );
}

/** A block eraser held at an angle — the Eraser tool. */
export function EraserIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M9.4 20.5 3.9 15a2 2 0 0 1 0-2.9l8.2-8.2a2 2 0 0 1 2.9 0l5.5 5.5a2 2 0 0 1 0 2.9l-8.2 8.2Z" />
      <path d="M8.1 7.9 16.1 15.9" />
      <path d="M9.4 20.5h11.1" />
    </svg>
  );
}

/** An arrow with a dashed loop behind it — the Select tool. */
export function SelectIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M4.5 3.4 13.9 20.2l2-6.3 6.3-2Z" />
      <path d="M3.2 8.6a10 10 0 0 1 .6-2.2" strokeDasharray="2 2" />
      <path d="M3.4 12.9a10 10 0 0 0 4.3 6.6" strokeDasharray="2 2" />
    </svg>
  );
}

/** A square with a circle over it — the Shapes tool. */
export function ShapesIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <rect x="3" y="8.5" width="9.5" height="9.5" rx="1" />
      <circle cx="15.5" cy="9.5" r="5.5" />
    </svg>
  );
}

/** A capital A on a writing line — the Text tool. */
export function TextIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M6 15.5 11.25 4.5h1.5L18 15.5" />
      <path d="M8 11.5h8" />
      <path d="M4.5 19.5h15" />
    </svg>
  );
}

/** A square with its corner turned up — the Sticky note tool. */
export function StickyIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M4 4.75h16v9.5l-5.75 5.5H4Z" />
      <path d="M20 14.25h-5.75v5.5" />
    </svg>
  );
}

/** A beam narrowing to a point — the Laser pointer. */
export function LaserIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M4.5 19.5 12 12" />
      <path d="M14.5 9.5 17 7" strokeWidth={2.4} />
      <path d="M19.5 4.5 18.6 5.4" strokeWidth={2.4} />
      <circle cx="12.9" cy="11.1" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** An arrow curving back on itself — Undo. */
export function UndoIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M3.75 8.25h9a5.25 5.25 0 0 1 0 10.5H7.5" />
      <path d="M7.5 4.5 3.75 8.25 7.5 12" />
    </svg>
  );
}

/** The same arrow, mirrored — Redo. */
export function RedoIcon({ size = 24, className }: IconProps) {
  return (
    <svg {...strokeProps(size)} className={className}>
      <path d="M20.25 8.25h-9a5.25 5.25 0 0 0 0 10.5h5.25" />
      <path d="M16.5 4.5 20.25 8.25 16.5 12" />
    </svg>
  );
}
